import React, { useState, useEffect, useMemo } from "react";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../common/GeneralComponent";
import dayjs from "dayjs";
import { fetchData } from "../../utils/functions";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

const WashInformationPanel = ({ washInfo: propWashInfo, onUpdate }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [dehs, setDehs] = useState([]);
  const [villages, setVillages] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [selectedDeh, setSelectedDeh] = useState(null);
  const [selectedVillage, setSelectedVillage] = useState(null);

  const [dateValue, setDateValue] = useState(null);
  const [timeValue, setTimeValue] = useState(null);
  const [washInfo, setWashInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isDqaApproved, setIsDqaApproved] = useState(false);
  const [isDqaRejected, setIsDqaRejected] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    respondent_name: "",
    respondent_cnic: "",
    respondent_line_number: "",
    respondent_role: "",
    respondent_role_other: "",
    mission: "",
    uc_name: "",
  });

  const isEmpty = (v) =>
    v === null || v === undefined || String(v).trim() === "";
  const isValidCNIC = (cnic) => /^\d{5}-\d{7}-\d{1}$/.test(cnic);

  const fetchDistricts = async () => {
    try {
      const response = await fetchData(
        "districts",
        "GET",
        null,
        {},
        {},
        navigate,
        "/wash",
        dispatch
      );
      setDistricts(response?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  const fetchTehsilsByDistrict = async (districtId) => {
    try {
      const response = await fetchData(
        `tehsils?district_id=${districtId}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "/wash",
        dispatch
      );
      setTehsils(response?.data || []);
    } catch (error) {
      console.error("Error fetching tehsils:", error);
      setTehsils([]);
    }
  };

  const fetchDehsByTehsil = async (tehsilId) => {
    try {
      const response = await fetchData(
        `dehs?tehsil_id=${tehsilId}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "/wash",
        dispatch
      );
      setDehs(response?.data || []);
    } catch (error) {
      console.error("Error fetching dehs:", error);
      setDehs([]);
    }
  };

  const fetchVillagesByDeh = async (dehId) => {
    try {
      const response = await fetchData(
        `villages?deh_id=${dehId}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "/wash",
        dispatch
      );
      setVillages(response?.data || []);
    } catch (error) {
      console.error("Error fetching villages:", error);
      setVillages([]);
    }
  };

  const getMissionOptions = () => {
    if (!selectedDeh) return [];
    const selectedDehData = dehs.find((d) => d.deh_id == selectedDeh);
    if (!selectedDehData) return [];
    const dehName = selectedDehData.deh_name;
    const missions = [];
    for (let i = 1; i <= 25; i++) {
      const missionNumber = i.toString().padStart(2, "0");
      missions.push({
        value: `${dehName}_M${missionNumber}`,
        label: `${dehName}_M${missionNumber}`,
      });
    }
    return missions;
  };

  const missionOptions = useMemo(
    () => getMissionOptions(),
    [selectedDeh, dehs]
  );

  const fetchWashInformation = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await fetchData(
        `wash-information/${id}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "/wash",
        dispatch
      );

      const data = response?.data;
      if (!data) return;

      setWashInfo(data);
      setFormData({
        respondent_name: data.respondent_name || "",
        respondent_cnic: data.respondent_cnic || "",
        respondent_line_number: data.respondent_line_number || "",
        respondent_role: data.respondent_role || "",
        respondent_role_other: data.respondent_role_other || "",
        mission: data.mission || "",
        uc_name: data.uc_name || "",
      });

      if (data.district_id) {
        setSelectedDistrict(data.district_id);
        await fetchTehsilsByDistrict(data.district_id);
      }
      if (data.tehsil_id) {
        setSelectedTehsil(data.tehsil_id);
        await fetchDehsByTehsil(data.tehsil_id);
      }
      if (data.deh_id) {
        setSelectedDeh(data.deh_id);
        await fetchVillagesByDeh(data.deh_id);
      }
      if (data.village_id) {
        // setSelectedVillage(data.village_id);
         setSelectedVillage(data.village_id);
      } else if (data.village) {
        setSelectedVillage(data.village_id);
      }
      if (data.date_time_of_survey) {
        const dt = dayjs(data.date_time_of_survey);
        if (dt.isValid()) {
          setDateValue(dt);
          setTimeValue(dt);
        }
      }

      setIsDqaApproved(
        data.verified_from_dqa === 1 || data.verified_from_dqa === "1"
      );
      setIsDqaRejected(
        data.verified_from_dqa === 2 || data.verified_from_dqa === "2"
      );
    } catch (error) {
      console.error("Error fetching wash info:", error);
      toast.error("Error loading wash information");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
  }, []);

  useEffect(() => {
    fetchWashInformation();
  }, [id]);

  const districtOptions = districts.map((d) => ({
    value: d.district_id,
    label: d.district_name,
  }));
  const tehsilOptions = tehsils.map((t) => ({
    value: t.tehsil_id,
    label: t.tehsil_name,
  }));
  const dehOptions = dehs.map((d) => ({ value: d.deh_id, label: d.deh_name }));
  const villageOptions = villages.map((v) => ({
    value: v.village_id,
    label: v.village_name,
  }));

  const respondentRoleOptions = [
    { label: "Community leader", value: "Community leader" },
    { label: "Local Government Official", value: "Local Government Official" },
    { label: "NGO Representative", value: "NGO Representative" },
    {
      label: "Operation and Maintenance Committee responsible",
      value: "Operation and Maintenance Committee responsible",
    },
    { label: "Others", value: "Others" },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleDropdownChange = (field, value) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    switch (field) {
      case "selectedDistrict":
        setSelectedDistrict(value);
        setSelectedTehsil(null);
        setSelectedDeh(null);
        setSelectedVillage(null);
        setTehsils([]);
        setDehs([]);
        setVillages([]);
        fetchTehsilsByDistrict(value);
        break;
      case "selectedTehsil":
        setSelectedTehsil(value);
        setSelectedDeh(null);
        setSelectedVillage(null);
        setDehs([]);
        setVillages([]);
        fetchDehsByTehsil(value);
        break;
      case "selectedDeh":
        setSelectedDeh(value);
        setSelectedVillage(null);
        setVillages([]);
        fetchVillagesByDeh(value);
        handleInputChange("mission", "");
        break;
      case "selectedVillage":
        setSelectedVillage(value);
        break;
      case "respondent_role":
        setFormData((prev) => ({
          ...prev,
          respondent_role: value,
          respondent_role_other:
            value === "Others" ? prev.respondent_role_other : "",
        }));
        break;
      case "mission":
        handleInputChange("mission", value);
        break;
      default:
        break;
    }
  };

  const validate = () => {
    const e = {};
    if (isEmpty(formData.respondent_name)) e.respondent_name = "Respondent name is required";
    if (
      isEmpty(formData.respondent_cnic) ||
      !isValidCNIC(formData.respondent_cnic)
    )
      e.respondent_cnic = "Invalid CNIC";
    if (isEmpty(formData.respondent_line_number))
      e.respondent_line_number = "Respondent line number is required";
    if (!dateValue) e.dateValue = " Date value is required";
    if (!timeValue) e.timeValue = "Time value required";
    if (isEmpty(formData.respondent_role)) e.respondent_role = "Respondent role is required";
    if (
      formData.respondent_role === "Others" &&
      isEmpty(formData.respondent_role_other)
    )
      e.respondent_role_other = "Specify other role";
    if (!selectedDistrict) e.selectedDistrict = "District is required";
    if (!selectedTehsil) e.selectedTehsil = "Tehsil is required";
    if (!selectedDeh) e.selectedDeh = "Deh is required";
    if (!selectedVillage) e.selectedVillage = "Village is required";
    if (isEmpty(formData.mission)) e.mission = "Mission is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdate = async () => {
    if (!validate()) {
      // toast.error("Please fix the highlighted errors.");
      return;
    }
    try {
      setUpdating(true);
      const updateData = {
        ...formData,
        district_id: selectedDistrict,
        tehsil_id: selectedTehsil,
        deh_id: selectedDeh,
        village_id: selectedVillage,
        date_time_of_survey:
          dateValue && timeValue
            ? dayjs(
                `${dateValue.format("YYYY-MM-DD")} ${timeValue.format(
                  "HH:mm:ss"
                )}`
              ).toISOString()
            : null,
      };
      const response = await fetchData(
        `wash-information/${id}`,
        "PUT",
        updateData,
        {},
        {},
        navigate,
        "/wash",
        dispatch
      );
      if (response?.success) {
        toast.success("Wash information updated successfully");
        if (onUpdate) onUpdate(response.data);
        await fetchWashInformation();
      } else {
        throw new Error(response?.message || "Update failed");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Update error");
    } finally {
      setUpdating(false);
    }
  };

  const Err = (name) =>
    errors?.[name] ? (
      <p className="text-red-600 text-sm mt-1">{errors[name]}</p>
    ) : null;

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        Loading wash information...
      </div>
    );

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-teal-700 text-xl font-semibold">
          Wash Information
        </h2>
        {isDqaApproved && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
            Approved from DQA
          </span>
        )}
        {isDqaRejected && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
            Rejected from DQA
          </span>
        )}
      </div>
      <form className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <ThinTextField
            label="Date of Survey"
            type="date"
            value={dateValue ? dateValue.format("YYYY-MM-DD") : ""}
            onChange={(e) =>
              setDateValue(e.target.value ? dayjs(e.target.value) : null)
            }
            readOnly
            disabled={isDqaApproved || isDqaRejected}
          />
          {Err("dateValue")}
        </div>
        <div>
          <ThinTextField
            label="Time of Survey"
            type="time"
            value={timeValue ? timeValue.format("HH:mm") : ""}
            onChange={(e) => {
              if (e.target.value) {
                const [h, m] = e.target.value.split(":");
                setTimeValue(dayjs().hour(Number(h)).minute(Number(m)));
              } else setTimeValue(null);
            }}
            readOnly
            disabled={isDqaApproved || isDqaRejected}
          />
          {Err("timeValue")}
        </div>
        <div>
          <ThinTextField
            label="Respondent Name"
            value={formData.respondent_name}
            onChange={(e) =>
              handleInputChange("respondent_name", e.target.value)
            }
            disabled={isDqaApproved || isDqaRejected}
          />
          {Err("respondent_name")}
        </div>
        <div>
          <ThinTextField
            label="Respondent CNIC"
            value={formData.respondent_cnic}
            onChange={(e) =>
              handleInputChange("respondent_cnic", e.target.value)
            }
            disabled={isDqaApproved || isDqaRejected}
          />
          {Err("respondent_cnic")}
        </div>
        <div>
          <ThinTextField
            label="Respondent Line Number"
            value={formData.respondent_line_number}
            onChange={(e) =>
              handleInputChange("respondent_line_number", e.target.value)
            }
            disabled={isDqaApproved || isDqaRejected}
          />
          {Err("respondent_line_number")}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="Respondent Role"
            options={respondentRoleOptions}
            value={formData.respondent_role}
            onChange={(v) => handleDropdownChange("respondent_role", v)}
            disabled={isDqaApproved || isDqaRejected}
          />
          {Err("respondent_role")}
        </div>
        {formData.respondent_role === "Others" && (
          <div>
            <ThinTextField
              label="Specify Other Role"
              value={formData.respondent_role_other}
              onChange={(e) =>
                handleInputChange("respondent_role_other", e.target.value)
              }
              disabled={isDqaApproved || isDqaRejected}
            />
            {Err("respondent_role_other")}
          </div>
        )}

        <div>
          {/* Location Dropdowns */}
          <ThinDropdownWithSearch
            label="District"
            options={districtOptions}
            value={selectedDistrict}
            onChange={(v) => handleDropdownChange("selectedDistrict", v)}
            disabled={isDqaApproved || isDqaRejected}
          />
          {Err("selectedDistrict")}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="Tehsil"
            options={tehsilOptions}
            value={selectedTehsil}
            onChange={(v) => handleDropdownChange("selectedTehsil", v)}
            disabled={!selectedDistrict || isDqaApproved || isDqaRejected}
          />
          {Err("selectedTehsil")}
        </div>
        <div>
          <ThinTextField
            label="UC"
            value={formData.uc_name}
            onChange={(e) => handleInputChange("uc_name", e.target.value)}
            disabled={isDqaApproved || isDqaRejected}
            isRequired={false}
          />
        </div>
        <div>
          <ThinDropdownWithSearch
            label="Deh"
            options={dehOptions}
            value={selectedDeh}
            onChange={(v) => handleDropdownChange("selectedDeh", v)}
            disabled={!selectedTehsil || isDqaApproved || isDqaRejected}
          />
          {Err("selectedDeh")}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="Mission"
            options={missionOptions}
            value={formData.mission}
            onChange={(v) => handleDropdownChange("mission", v)}
            disabled={!selectedDeh || isDqaApproved || isDqaRejected}
          />
          {Err("mission")}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="Village"
            options={villageOptions}
            value={selectedVillage}
            onChange={(v) => handleDropdownChange("selectedVillage", v)}
            disabled={!selectedDeh || isDqaApproved || isDqaRejected}
          />
          {Err("selectedVillage")}
        </div>
      </form>

      {!isDqaApproved && !isDqaRejected && (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={updating}
            className={`${
              updating ? "bg-gray-400" : "bg-teal-600 hover:bg-teal-700"
            } text-white px-4 py-2 rounded-lg transition duration-200 font-medium`}
          >
            {updating ? "Updating..." : "Update"}
          </button>
        </div>
      )}
    </>
  );
};

export default WashInformationPanel;
